import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
} from "lucide-react";
import { PaintMode, PaintShape } from "@drillflo/drillcore/lib/2d/Engine";
import Comments from "./comments.tsx";
import PageAnalysis from "../elements/pageAnalysis.tsx";
import {
  useFieldEditorData,
  useDrillViewData,
  useDrillDocumentData,
  usePlaybackData,
  useUserData,
  usePerformerManipulationData,
  useCollaborationData,
} from "../hooks/useFieldEditorShared.tsx";
import { HashType } from "@drillflo/drillcore/lib/Drill";

export default function FieldEditorRightNav() {
  // Get data from context hooks
  const { fieldEditorData } = useFieldEditorData();
  const {
    drill,
    performerPaintingProps,
    setPerformerPaintingProps,
    viewOptions,
    updateViewOptions,
    confirmShape,
    cancelShape,
  } = useDrillViewData();
  const { drillDocumentData, updateDrillDocumentData } = useDrillDocumentData();

  const { drillModifiedByCollaboration } = useCollaborationData();

  const { playbackState } = usePlaybackData();
  const currentUser = useUserData();

  const {
    alignPerformersToLeft,
    alignPerformersToCenter,
    alignPerformersToRight,
    alignPerformersToTop,
    alignPerformersHorizontally,
    alignPerformersToBottom,
    handleRotateClockwise,
    handleRotateCounterClockwise,
  } = usePerformerManipulationData();

  // Default rightWidth if not provided
  const rightWidth = 300;
  const [snapToSteps, setSnapToSteps] = React.useState<boolean>(
    viewOptions.snapToSteps ?? false,
  );

  useEffect(() => {
    if (viewOptions && snapToSteps === viewOptions.snapToSteps) {
      return;
    }

    // Only pass snapToSteps — spreading undefined hashType/endzones was clearing
    // viewOptions and resetting hash & endzones after reload.
    updateViewOptions({
      snapToSteps: snapToSteps,
    });
  }, [snapToSteps, updateViewOptions, viewOptions]);

  // A custom property stored on the page custom data itself
  const [
    currentPageDirections,
    setCurrentPageDirectionsAfterPlaybackStatechange,
  ] = useState<string>("");

  useEffect(() => {
    if (drill?.pages[playbackState.currentPage]) {
      const directions = (drill?.pages[playbackState.currentPage].getCustomData(
        "directions",
      ) ?? "") as string;

      if (directions !== currentPageDirections) {
        setCurrentPageDirectionsAfterPlaybackStatechange(directions);
      }
    }
  }, [
    playbackState,
    drill,
    drillModifiedByCollaboration,
    currentPageDirections,
  ]);

  const setCurrentPageDirections = useCallback(
    (directions: string) => {
      if (drill?.pages[playbackState.currentPage]) {
        drill?.pages[playbackState.currentPage].setCustomData(
          "directions",
          directions,
        );
        setCurrentPageDirectionsAfterPlaybackStatechange(directions);
      }
    },
    [drill, playbackState],
  );

  // When the drill reloads from collaboration, display options live on the engine
  // (serialized displayOptions), not reliably on customData.hashMarkType — that field
  // can be missing or legacy (e.g. "standard"). Mirror engine → View + React state.
  useEffect(() => {
    if (!drill?.engine) {
      return;
    }
    const nextHash = drill.engine.hashType;
    const nextEndzones = drill.engine.endZones;

    if (
      viewOptions.hashType === nextHash &&
      viewOptions.endzones === nextEndzones
    ) {
      return;
    }

    updateViewOptions({
      hashType: nextHash,
      endzones: nextEndzones,
    });
  }, [
    drillModifiedByCollaboration,
    drill,
    updateViewOptions,
    viewOptions.hashType,
    viewOptions.endzones,
  ]);

  return (
    <div
      className="bg-white border-l right-panel flex flex-col h-full min-h-0 shrink-0 box-border"
      style={{
        width: `${rightWidth}px`,
        minWidth: "100px",
      }}
    >
      {/* Panel contents */}
      <div className="p-1 flex-1 min-h-0 min-w-0 overflow-y-auto">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Properties</h3>{" "}
        {fieldEditorData.editMode === "analyze" && drill && (
          <PageAnalysis drill={drill} playbackState={playbackState} />
        )}
        {fieldEditorData.editMode === "comment" && (
          <Comments
            drill={drill}
            playbackState={playbackState}
            currentUser={currentUser}
          />
        )}
        {fieldEditorData.editMode === "SELECT_AND_MOVE" && (
          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-medium text-gray-500 mb-2">
                Alignment
              </h4>
              <div className="grid grid-cols-3 gap-0 w-3/5 m-auto">
                <button
                  onClick={alignPerformersToLeft}
                  className="p-1.5 rounded border border-gray-200 m-auto hover:bg-gray-50"
                  title="Align Left"
                >
                  <AlignStartVertical height={16} />
                </button>
                <button
                  onClick={alignPerformersToCenter}
                  className="p-1.5 rounded border border-gray-200 m-auto hover:bg-gray-50"
                  title="Align Center"
                >
                  <AlignCenterVertical height={16} />
                </button>
                <button
                  onClick={alignPerformersToRight}
                  className="p-1.5 rounded border border-gray-200 m-auto hover:bg-gray-50"
                  title="Align Right"
                >
                  <AlignEndVertical height={16} />
                </button>
                <button
                  onClick={alignPerformersToTop}
                  className="p-1.5 rounded border border-gray-200 m-auto hover:bg-gray-50"
                  title="Align Top"
                >
                  <AlignStartHorizontal height={16} />
                </button>
                <button
                  onClick={alignPerformersHorizontally}
                  className="p-1.5 rounded border border-gray-200 m-auto hover:bg-gray-50"
                  title="Align Middle"
                >
                  <AlignCenterHorizontal height={16} />
                </button>
                <button
                  onClick={alignPerformersToBottom}
                  className="p-1.5 rounded border border-gray-200 m-auto hover:bg-gray-50"
                  title="Align Bottom"
                >
                  <AlignEndHorizontal height={16} />
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-medium text-gray-500 mb-2">
                Rotation
              </h4>
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={handleRotateCounterClockwise}
                  className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 flex items-center justify-center"
                  title="Rotate Counter-Clockwise"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                    <path d="M3 3v5h5"></path>
                  </svg>
                </button>
                <button
                  onClick={handleRotateClockwise}
                  className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 flex items-center justify-center"
                  title="Rotate Clockwise"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                    <path d="M21 3v5h-5"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div className="pt-2 border-t">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">
                  Snap to grid
                </div>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="snapToGrid"
                      checked={!snapToSteps}
                      onChange={() => setSnapToSteps(false)}
                      className="rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Off</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="snapToGrid"
                      checked={
                        snapToSteps &&
                        viewOptions?.movementSensitivity === "FULL_STEP"
                      }
                      onChange={() => {
                        setSnapToSteps(true);
                        updateViewOptions({
                          movementSensitivity: "FULL_STEP",
                        });
                      }}
                      className="rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Full step
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="snapToGrid"
                      checked={
                        snapToSteps &&
                        viewOptions?.movementSensitivity === "HALF_STEP"
                      }
                      onChange={() => {
                        setSnapToSteps(true);
                        updateViewOptions({
                          movementSensitivity: "HALF_STEP",
                        });
                      }}
                      className="rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Half step
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={viewOptions.showMovementArrows}
                  onChange={(e) =>
                    updateViewOptions({ showMovementArrows: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Show movement arrows
                </span>
              </label>
            </div>

            <div>
              <h4 className="text-md pt-2 font-medium text-gray-500 mb-2">
                Page Directions
              </h4>
              <label className="flex items-center">
                <input
                  type="textarea"
                  value={currentPageDirections}
                  onChange={(e) => setCurrentPageDirections(e.target.value)}
                  className="rounded border-gray-300 border border-2 w-full h-12 mb-16 text-blue-600 focus:ring-blue-500 text-sm"
                />
              </label>
            </div>
          </div>
        )}
        {fieldEditorData.editMode === "PAINT_PERFORMERS" && (
          <div className="space-y-4">
            <div>
              {/* <label
                        htmlFor="performerSymbol"
                        className="block text-xs font-medium text-gray-500 mb-1"
                      >
                        Performer Symbol
                      </label>
                      <input
                        type="text"
                        maxLength={1}
                        id="performerSymbol"
                        value={newPerformerSymbol}
                        onChange={(e) =>
                          setNewPerformerSymbol(e.target.value.toUpperCase())
                        }
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      /> */}
            </div>

            <div>
              <label
                htmlFor="paintMode"
                className="block text-xs font-medium text-gray-500 mb-1"
              >
                Paint Mode
                <span
                  className="ml-1 inline-block w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs font-bold text-center leading-4 cursor-default"
                  title="Select Performers and choose to free draw or use a shape"
                >
                  i
                </span>
              </label>
              <select
                id="paintMode"
                onChange={(e) =>
                  setPerformerPaintingProps((prev) => ({
                    ...prev,
                    paintMode: Number(e.target.value),
                  }))
                }
                value={performerPaintingProps.paintMode}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                {Object.entries(PaintMode)
                  .filter(
                    ([key, value]) =>
                      isNaN(Number(key)) && value !== PaintMode.TEXT,
                  )
                  .map(([key, value]) => (
                    <option key={key} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </option>
                  ))}
              </select>
            </div>

            {performerPaintingProps.paintMode === PaintMode.SHAPE && (
              <div>
                <label
                  htmlFor="paintShape"
                  className="block text-xs font-medium text-gray-500 mb-1"
                >
                  Shape
                </label>
                <select
                  id="paintShape"
                  onChange={(e) =>
                    setPerformerPaintingProps((prev) => ({
                      ...prev,
                      paintShape: Number(e.target.value),
                    }))
                  }
                  value={performerPaintingProps.paintShape}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  {Object.entries(PaintShape)
                    .filter(([key, value]) => isNaN(Number(key)))
                    .map(([key, value]) => (
                      <option key={key} value={value}>
                        {key.charAt(0) + key.slice(1).toLowerCase()}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {performerPaintingProps.paintMode === PaintMode.SHAPE && (
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    id="paintShapeEvenDimensions"
                    checked={performerPaintingProps.paintShapeEvenDimensions}
                    onChange={(e) =>
                      setPerformerPaintingProps((prev) => ({
                        ...prev,
                        paintShapeEvenDimensions: e.target.checked,
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Even Dimensions
                  </span>
                </label>
              </div>
            )}

            <div>
              <label
                htmlFor="drawSnapToSteps"
                className="block text-xs font-medium text-gray-500 mb-1"
              >
                Snap to Step Size
              </label>
              <input
                type="number"
                id="drawSnapToSteps"
                value={performerPaintingProps.drawSnapToSteps}
                min={0}
                max={5}
                step={0.5}
                onChange={(e) =>
                  setPerformerPaintingProps((prev) => ({
                    ...prev,
                    drawSnapToSteps: Number(e.target.value),
                  }))
                }
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              />
            </div>

            {performerPaintingProps.paintMode === PaintMode.SHAPE && (
              <div className="space-y-2">
                <button
                  onClick={confirmShape}
                  className="w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Confirm Shape
                </button>
                <button
                  onClick={cancelShape}
                  className="w-full py-1.5 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md transition-colors"
                >
                  Cancel Shape
                </button>
              </div>
            )}

            <div>
              <label
                htmlFor="performerPaintStepSeperation"
                className="block text-xs font-medium text-gray-500 mb-1"
              >
                Step Separation
              </label>
              <input
                type="number"
                id="performerPaintStepSeperation"
                value={performerPaintingProps.performerPaintStepSeperation}
                min={-1}
                max={50}
                onChange={(e) =>
                  setPerformerPaintingProps((prev) => ({
                    ...prev,
                    performerPaintStepSeperation: Number(e.target.value),
                  }))
                }
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              {/* <label
                        htmlFor="numPerformersToPaint"
                        className="block text-xs font-medium text-gray-500 mb-1"
                      >
                        {performersSelected.length > 0
                          ? "Painting Selected Performers"
                          : "Number of Performers to Paint"}
                      </label>
                      <input
                        type="number"
                        id="numPerformersToPaint"
                        value={numPerformersToPaint}
                        disabled={performersSelected.length > 0}
                        min={1}
                        max={50}
                        onChange={(e) =>
                          setNumPerformersToPaint(Number(e.target.value))
                        }
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      /> */}
            </div>
          </div>
        )}
        {fieldEditorData.editMode === "PAN" && (
          <div className="space-y-4">
            <div className="pt-2 border-t">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={viewOptions.endzones}
                  onChange={(e) => {
                    const endzones = e.target.checked;
                    updateDrillDocumentData({
                      drillOptions: {
                        endzones,
                        hashType: String(
                          viewOptions.hashType ??
                            drill?.engine?.hashType ??
                            HashType.HIGH_SCHOOL,
                        ),
                        performerSymbolStyle:
                          drillDocumentData?.drillOptions
                            ?.performerSymbolStyle ??
                          String(drill?.engine?.performerSymbolStyle ?? "DOT"),
                        performerCount:
                          drillDocumentData?.drillOptions?.performerCount ?? 0,
                      },
                    });
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Show endzones
                </span>
              </label>
            </div>

            <div>
              <label
                htmlFor="hashType"
                className="block text-xs font-medium text-gray-500 mb-1"
              >
                Hash Type
              </label>
              <select
                id="hashType"
                value={viewOptions.hashType}
                onChange={(e) => {
                  const hashType =
                    HashType[e.target.value as keyof typeof HashType];
                  updateDrillDocumentData({
                    drillOptions: {
                      hashType: String(hashType),
                      endzones:
                        viewOptions.endzones ??
                        drill?.engine?.endZones ??
                        false,
                      performerSymbolStyle:
                        drillDocumentData?.drillOptions?.performerSymbolStyle ??
                        String(drill?.engine?.performerSymbolStyle ?? "DOT"),
                      performerCount:
                        drillDocumentData?.drillOptions?.performerCount ?? 0,
                    },
                  });
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value={HashType.COLLEGE}>College</option>
                <option value={HashType.HIGH_SCHOOL}>High School</option>
                <option value={HashType.HIGH_SCHOOL_PERFECT}>
                  High School Perfect
                </option>
              </select>
            </div>
          </div>
        )}
        {/* {editMode === "analyze" && (
          <PageAnalysis pagePerformerStepSizes={pagePerformerStepSizes} />
        )} */}
      </div>
      <div className="text-center text-sm text-gray-500 py-4 shrink-0 border-t">
        <a
          href="https://help.drillflo.com/report-bug"
          target="_blank"
          className="text-blue-500 hover:text-blue-600"
          rel="noopener noreferrer"
        >
          Report a Bug
        </a>
      </div>
    </div>
  );
}
